import React, { useEffect, useState, useRef } from "react";
import axios from "../../api/axios";
import "./styles/AdminGallery.css";

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [altText, setAltText] = useState("");
  const [error, setError] = useState("");
  const [csrfToken, setCsrfToken] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [editForm, setEditForm] = useState({
    alt_text: "",
    is_active: 1,
    service_area_id: "", // 🆕 new field
  });

  const [draggedId, setDraggedId] = useState(null);
  const [addImagePreview, setAddImagePreview] = useState(null);
  const originalOrderRef = useRef([]);

  const [serviceAreas, setServiceAreas] = useState([]);



  const [orderedImages, setOrderedImages] = useState([]);

  const [addingNew, setAddingNew] = useState(false);
  const [addForm, setAddForm] = useState({
    file: null,
    alt_text: "",
    is_active: 1,
    service_area_id: "", // 🆕 new field
  });


  const cardRefs = useRef({});

  // CSRF
  useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find(row => row.startsWith("csrfToken="))
      ?.split("=")[1];
    if (token) setCsrfToken(token);
  }, []);

  // Fetch images on mount
  useEffect(() => { fetchGalleryImages(); }, []);

  const fetchGalleryImages = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/public/gallery/images");
      setImages(data.images || []);
      setOrderedImages((data.images || []).sort((a, b) => a.sort_order - b.sort_order));
    } catch {
      setError("Failed to load gallery images.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        const res = await axios.get("/api/public/service-areas", { withCredentials: true });
        const activeAreas = (res.data.service_areas || []).filter(a => a.is_active);
        setServiceAreas(activeAreas);
      } catch (err) {
        console.error("🔥 Error fetching service areas:", err);
      }
    };
    fetchServiceAreas();
  }, []);


  // Upload
  const handleFileChange = e => setNewImage(e.target.files[0]);
  
  const handleUpload = async (e) => {
  e.preventDefault();
  setError("");
  if (!addForm.file) return setError("Please select an image.");
  if (!addForm.alt_text.trim()) return setError("Alt text is required.");
  if (!csrfToken) return setError("CSRF token missing.");

  const formData = new FormData();
  formData.append("image", addForm.file);
  formData.append("alt_text", addForm.alt_text);
  formData.append("is_active", addForm.is_active ? 1 : 0);
  formData.append("service_area_id", addForm.service_area_id || "");


  setUploading(true);
  try {
    await axios.post("/api/admin/gallery/images", formData, {
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
        "x-csrf-token": csrfToken,
      },
    });
    setAddingNew(false);
    setAddForm({ file: null, alt_text: "", is_active: 1 });
    setAddImagePreview(null); // <-- Clear preview
    fetchGalleryImages();
  } catch {
    setError("Failed to upload image.");
  } finally {
    setUploading(false);
  }
};


  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    if (!csrfToken) return setError("CSRF token missing.");
    try {
      await axios.delete(`/api/admin/gallery/images/${id}`, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });
      setImages(imgs => imgs.filter(img => img.id !== id));
    } catch {
      setError("Failed to delete image.");
    }
  };



  // Edit
  const handleEdit = (img) => {
    setEditingId(img.id);
    setEditForm({
      alt_text: img.alt_text,
      is_active: !!img.is_active,
      service_area_id: img.service_area_id || "", // 🆕 prefill
    });
  };


  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({ alt_text: "", is_active: 1 });
  };

  const handleEditSave = async (id) => {
    if (!csrfToken) return setError("CSRF token missing.");
    if (!editForm.alt_text.trim()) {
      setError("Alt text is required.");
      return;
    }
    setError("");
    try {
      await axios.patch(`/api/admin/gallery/images/${id}`, editForm, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });

      // 🔧 FIX HERE
      setImages(imgs =>
        imgs.map(img =>
          img.id === id
            ? {
                ...img,
                alt_text: editForm.alt_text,
                is_active: +editForm.is_active,
                service_area_id: editForm.service_area_id || null,
              }
            : img
        )
      );

      setEditingId(null);
      setEditForm({ alt_text: "", is_active: 1, service_area_id: "" }); // also reset form completely
    } catch {
      setError("Failed to save changes.");
    }
  };


  // Reorder logic (same as reviews, no drag on edit/upload)
  const handleDragStart = (e, idx) => {
    if (editingId || uploading) return false;
    setDraggedId(images[idx].id);

    const cardNode = cardRefs.current[images[idx].id];
    if (cardNode && e.dataTransfer) {
      // Use offset to keep card ghost under cursor
      e.dataTransfer.setDragImage(cardNode, dragOffset.x, dragOffset.y);
    }
    originalOrderRef.current = images.map(img => img.id);
    return true;
  };



  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e, id) => {
    const card = cardRefs.current[id];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };



  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (editingId || uploading || draggedId === null) return;

    // Find the index of the dragged image in the *current* images array
    const currentIndex = images.findIndex(img => img.id === draggedId);
    if (currentIndex === -1 || currentIndex === idx) return;

    const newImages = [...images];
    const [moved] = newImages.splice(currentIndex, 1);
    newImages.splice(idx, 0, moved);

    setImages(newImages);
    // Note: do NOT update draggedId or any index here!
  };



  const handleDrop = async () => {
    setDraggedId(null);

    // Compare current order to original order
    const currentOrder = images.map(img => img.id);
    const originalOrder = originalOrderRef.current;

    // Only send request if order changed
    const orderChanged =
      currentOrder.length !== originalOrder.length ||
      currentOrder.some((id, idx) => id !== originalOrder[idx]);

    if (!orderChanged) {
      return; // No request needed
    }

    // ...same as before
    const reorderPayload = images.map((img, idx) => ({
      id: img.id,
      sort_order: idx + 1,
    }));
    try {
      await axios.patch(
        "/api/admin/gallery/images/reorder",
        { order: reorderPayload },
        { headers: { "x-csrf-token": csrfToken }, withCredentials: true }
      );
      fetchGalleryImages();
    } catch {
      setError("Failed to update image order.");
    }
  };

  function isMobile() {
    return window.innerWidth <= 700; // Adjust to your mobile breakpoint
  }


  const moveImage = (index, direction) => {
  const newIdx = index + direction;
  if (newIdx < 0 || newIdx >= images.length) return;
  const newImages = [...images];
  [newImages[index], newImages[newIdx]] = [newImages[newIdx], newImages[index]];
  setImages(newImages);

  // Update backend immediately
  const reorderPayload = newImages.map((img, idx) => ({
    id: img.id,
    sort_order: idx + 1
  }));
  axios.patch(
    "/api/admin/gallery/images/reorder",
    { order: reorderPayload },
    { headers: { "x-csrf-token": csrfToken }, withCredentials: true }
  ).catch(() => setError("Failed to update image order."));
};

const moveImageTo = (index, targetIdx) => {
  if (
    index === targetIdx ||
    index < 0 ||
    index >= images.length ||
    targetIdx < 0 ||
    targetIdx >= images.length
  )
    return;
  const newImages = [...images];
  const [moved] = newImages.splice(index, 1);
  newImages.splice(targetIdx, 0, moved);
  setImages(newImages);

  // Immediately update backend order
  const reorderPayload = newImages.map((img, idx) => ({
    id: img.id,
    sort_order: idx + 1,
  }));
  axios
    .patch(
      "/api/admin/gallery/images/reorder",
      { order: reorderPayload },
      { headers: { "x-csrf-token": csrfToken }, withCredentials: true }
    )
    .catch(() => setError("Failed to update image order."));
};



  const disableAllDrag = addingNew || !!editingId || uploading;

 return (
  <div className="admin-messages-container">
    <h2 className="messages-title">Gallery Image Manager</h2>

    <div style={{ marginBottom: 20 }}>
      <button
        className="btn add-btn"
        onClick={() => {
          setAddingNew(true);
          setAddForm({ file: null, alt_text: "", is_active: 1 });
          setError("");
        }}
        disabled={addingNew || editingId}
      >
        <i className="fa-solid fa-plus"></i> Add Image
      </button>
    </div>
    {error && <div className="admin-gallery-error">{error}</div>}

    {loading ? (
      <p className="messages-loading">Loading images...</p>
    ) : (
      <>
        {/* Only show reorder instructions if there are images */}
        {images.length > 0 && (
          <div
            style={{
              marginBottom: '2.5rem',
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: 'center'
            }}
          >
            <i className="fa-solid fa-bars" style={{ opacity: 0.7 }} aria-hidden="true" />
            <span style={{ fontSize: 15, color: "#555" }}>
              Drag and drop to reorder the gallery.
            </span>
          </div>
        )}

        {/* Always render the grid if addingNew or images exist */}
        {(addingNew || images.length > 0) && (
          <div className="gallery-cards-grid">
            {addingNew && (
              <>
                <div className="gallery-image-card new-card">
                  <div className="gallery-edit-fields">
                    {!addForm.file ? (
                      <label className="custom-dropzone">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => {
                            const file = e.target.files[0];
                            setAddForm(f => ({ ...f, file }));
                            if (file) {
                              setAddImagePreview(URL.createObjectURL(file));
                            } else {
                              setAddImagePreview(null);
                            }
                          }}
                          disabled={uploading}
                          style={{ display: "none" }}
                        />
                        <div className="dropzone-content">
                          <i className="fa-solid fa-image"></i>
                          <div className="dropzone-text">
                            Click or drag image here
                          </div>
                        </div>
                      </label>
                    ) : (
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                        marginBottom: '5px'
                      }}>
                        <div className="preview-img-wrapper" style={{ position: "relative", width: "100%" }}>
                          <img
                            src={addImagePreview}
                            alt={addForm.alt_text || "Preview"}
                            className="gallery-image-preview"
                            style={{ marginBottom: 8, width: "100%", display: "block" }}
                          />
                          <button
                            type="button"
                            className="preview-remove-btn"
                            aria-label="Remove image"
                            onClick={() => {
                              setAddForm(f => ({ ...f, file: null }));
                              setAddImagePreview(null);
                            }}
                          >
                            <i className="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                        <div className="uploaded-filename" style={{
                          fontSize: 14,
                          color: "#666",
                          marginTop: 6,
                          marginBottom: 8,
                          wordBreak: "break-all"
                        }}>
                          <i className="fa-solid fa-image" style={{ marginRight: 6 }} /> {addForm.file.name}
                        </div>
                      </div>
                    )}

                    <input
                      type="text"
                      placeholder="Alt text (required for accessibility)"
                      value={addForm.alt_text}
                      onChange={e =>
                        setAddForm(f => ({ ...f, alt_text: e.target.value }))
                      }
                      disabled={uploading}
                    />
                    <label className="visible-checkbox">
                      <input
                        type="checkbox"
                        checked={!!addForm.is_active}
                        onChange={e =>
                          setAddForm(f => ({ ...f, is_active: e.target.checked }))
                        }
                        disabled={uploading}
                      />{" "}
                      <span>Show on gallery page?</span>
                    </label>
                    <div className="gallery-edit-actions">
                      <button
                        className="btn save-btn"
                        disabled={uploading}
                        onClick={async () => {
                          setError("");
                          if (!addForm.file) return setError("Please select an image.");
                          if (!addForm.alt_text.trim()) return setError("Alt text is required.");
                          if (!csrfToken) return setError("CSRF token missing.");
                          setUploading(true);
                          try {
                            const formData = new FormData();
                            formData.append("image", addForm.file);
                            formData.append("alt_text", addForm.alt_text);
                            formData.append("is_active", addForm.is_active ? 1 : 0);
                            formData.append("service_area_id", addForm.service_area_id || "");

                            await axios.post("/api/admin/gallery/images", formData, {
                              withCredentials: true,
                              headers: {
                                "Content-Type": "multipart/form-data",
                                "x-csrf-token": csrfToken,
                              },
                            });
                            setAddingNew(false);
                            setAddForm({ file: null, alt_text: "", is_active: 1 });
                            setAddImagePreview(null); // <-- Clear preview
                            fetchGalleryImages();
                          } catch {
                            setError("Failed to upload image.");
                          } finally {
                            setUploading(false);
                          }
                        }}
                        style={{ marginRight: 8 }}
                      >
                        <i className="fa-solid fa-floppy-disk"></i> Save
                      </button>
                      <button
                        className="btn cancel-btn"
                        onClick={() => {
                          setAddingNew(false);
                          setAddForm({ file: null, alt_text: "", is_active: 1 });
                          setAddImagePreview(null); // <-- Clear preview
                          setError("");
                        }}
                      >
                        <i className="fa-solid fa-xmark"></i> Cancel
                      </button>
                    </div>
                  </div>
                </div>
                <div className="gallery-vertical-separator" aria-hidden="true" />
              </>
            )}

            {images.map((img, idx) => {
              const editing = editingId === img.id;
              return (
                <div
                  className={`gallery-image-card${draggedId === img.id ? " dragging" : ""}`}
                  key={img.id}
                  ref={el => (cardRefs.current[img.id] = el)}
                  draggable={!disableAllDrag}
                  onMouseDown={e => handleMouseDown(e, img.id)}
                  onDragStart={e => !disableAllDrag ? handleDragStart(e, idx) : e.preventDefault()}
                  onDragOver={e => !disableAllDrag ? handleDragOver(e, idx) : e.preventDefault()}
                  onDrop={!disableAllDrag ? handleDrop : undefined}
                >
                  <img
                    src={`${process.env.REACT_APP_API_URL}/uploads/gallery/${img.image_url}`}
                    alt={img.alt_text}
                    className="gallery-image-preview"
                  />
                  {editing ? (
                    <div className="gallery-edit-fields">
                      <input
                        type="text"
                        value={editForm.alt_text}
                        onChange={e =>
                          setEditForm(f => ({ ...f, alt_text: e.target.value }))
                        }
                        placeholder="Alt text"
                      />
                      <div style={{ margin: "8px 0" }}>
                        <label>
                          <span style={{ display: "block", marginBottom: 4 }}>Service Area</span>
                          <select
                            value={editForm.service_area_id || ""}
                            onChange={(e) =>
                              setEditForm(f => ({
                                ...f,
                                service_area_id: e.target.value || "",
                              }))
                            }
                          >
                            <option value="">— None —</option>
                            {serviceAreas.map(area => (
                              <option key={area.id} value={area.id}>
                                {area.city}, {area.state}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <label className="visible-checkbox">
                        <input
                          type="checkbox"
                          checked={!!editForm.is_active}
                          onChange={e =>
                            setEditForm(f => ({ ...f, is_active: e.target.checked }))
                          }
                        />{" "}
                        Show on gallery page?
                      </label>
                      
                      <div className="gallery-edit-actions">
                        <button
                          className="btn save-btn"
                          onClick={() => handleEditSave(img.id)}
                          style={{ marginRight: 8 }}
                        >
                          <i className="fa-solid fa-floppy-disk"></i> Save
                        </button>
                        <button
                          className="btn cancel-btn"
                          onClick={handleEditCancel}
                        >
                          <i className="fa-solid fa-xmark"></i> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="gallery-image-meta">
                        <div className="gallery-alt" title={img.alt_text}>
                          {img.alt_text.length > 60 ? img.alt_text.slice(0, 60) + "…" : img.alt_text}
                        </div>
                        <div className="gallery-active">
                          {img.is_active ? "Visible" : (
                            <>
                              <i className="fa-solid fa-eye-slash"></i> Hidden
                            </>
                          )}
                        </div>
                      </div>
                      {!editingId && !addingNew && (
                        <div className="reorder-buttons" style={{ display: isMobile() ? 'flex' : 'none', gap: 4 }}>
                          <button
                            className="btn reorder-btn"
                            onClick={() => moveImageTo(idx, 0)}
                            disabled={idx === 0}
                            aria-label="Move to top"
                            tabIndex={0}
                          >
                            <i className="fa-solid fa-arrow-up-short-wide"></i>
                          </button>
                          <button
                            className="btn reorder-btn"
                            onClick={() => moveImage(idx, -1)}
                            disabled={idx === 0}
                            aria-label="Move up"
                            tabIndex={0}
                          >
                            <i className="fa-solid fa-arrow-up"></i>
                          </button>
                          <button
                            className="btn reorder-btn"
                            onClick={() => moveImage(idx, 1)}
                            disabled={idx === images.length - 1}
                            aria-label="Move down"
                            tabIndex={0}
                          >
                            <i className="fa-solid fa-arrow-down"></i>
                          </button>
                          <button
                            className="btn reorder-btn"
                            onClick={() => moveImageTo(idx, images.length - 1)}
                            disabled={idx === images.length - 1}
                            aria-label="Move to bottom"
                            tabIndex={0}
                          >
                            <i className="fa-solid fa-arrow-down-wide-short"></i>
                          </button>
                        </div>
                      )}


                      <div className="gallery-image-actions">
                        <button
                          className="btn edit-btn"
                          onClick={() => handleEdit(img)}
                          style={{ marginRight: 8 }}
                          disabled={editingId || uploading}
                          title="Edit Alt Text / Show"
                        >
                          <i className="fa-solid fa-pen-to-square"></i>
                        </button>
                        <button
                          className="btn delete-btn"
                          onClick={() => handleDelete(img.id)}
                          disabled={editingId || uploading}
                          title="Delete"
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                        {/* Drag handle */}
                        {!disableAllDrag && (
                          <span className="drag-handle" title="Drag to reorder" style={{ marginLeft: 8 }}>
                            <i className="fa-solid fa-bars"></i>
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Show empty state only if not adding and gallery is empty */}
        {!addingNew && images.length === 0 && (
          <p className="messages-empty">No images in gallery.</p>
        )}
      </>
    )}
  </div>
);

};

export default AdminGallery;
